/**
 * ChatThreadSwitcher — header dropdown for the Composer AI Chat panel.
 * Lists threads for the active Piece, supports rename / archive / new thread.
 */
import { useState } from "react";
import { Check, ChevronDown, Pencil, Plus, Archive, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import type { ComposerChatThread } from "@/lib/composerChatApi";
import { composerChatApi } from "@/lib/composerChatApi";
import { cn } from "@/lib/utils";

type Props = {
  workspaceId: string;
  pieceId: string;
  threads: ComposerChatThread[];
  activeThreadId: string | null;
  onSelect: (threadId: string) => void;
  onChanged: () => void;
  onDeleted?: (deletedThreadId: string) => void;
  disabled?: boolean;
};

const threadLabel = (t: ComposerChatThread): string => {
  const title = t.title?.trim();
  if (title) return title;
  // Server auto-titles from the first user message on the first turn. Until
  // that write lands (or for legacy untitled threads), derive a preview from
  // the most-recent snippet so the dropdown isn't full of "Untitled".
  const snippet = t.last_message_snippet?.trim();
  if (snippet) return snippet.length > 60 ? `${snippet.slice(0, 57)}…` : snippet;
  return "Untitled thread";
};

export const ChatThreadSwitcher = ({
  workspaceId,
  pieceId,
  threads,
  activeThreadId,
  onSelect,
  onChanged,
  onDeleted,
  disabled,
}: Props) => {
  const [creating, setCreating] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const active = threads.find((t) => t.id === activeThreadId) ?? null;
  const visible = threads.filter((t) => showArchived || !t.archived_at);

  const handleNew = async () => {
    setCreating(true);
    try {
      const { thread } = await composerChatApi.createThread(workspaceId, pieceId);
      onChanged();
      onSelect(thread.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not create thread");
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (t: ComposerChatThread) => {
    try {
      if (t.archived_at) {
        await composerChatApi.unarchiveThread(workspaceId, pieceId, t.id);
        toast.success("Thread unarchived");
      } else {
        await composerChatApi.archiveThread(workspaceId, pieceId, t.id);
        toast.success("Thread archived");
      }
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not archive thread");
    }
  };

  const handleDelete = async (t: ComposerChatThread) => {
    const label = t.title?.trim() || "this chat";
    const ok = window.confirm(
      `Delete "${label}"? This can't be undone.`,
    );
    if (!ok) return;
    try {
      await composerChatApi.deleteThread(workspaceId, pieceId, t.id);
      toast.success("Thread deleted");
      if (onDeleted) onDeleted(t.id);
      else onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not delete thread");
    }
  };


  const startRename = () => {
    if (!active) return;
    setRenameValue(active.title ?? "");
    setRenameOpen(true);
  };

  const submitRename = async () => {
    if (!active) return;
    const next = renameValue.trim();
    if (!next) {
      toast.error("Title can't be empty");
      return;
    }
    setRenaming(true);
    try {
      await composerChatApi.renameThread(workspaceId, pieceId, active.id, next);
      onChanged();
      setRenameOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not rename");
    } finally {
      setRenaming(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            className="h-7 max-w-[220px] gap-1 px-2 text-xs"
            title="Switch chat thread"
          >
            <span className="truncate">{active ? threadLabel(active) : "New thread"}</span>
            {active?.unread ? (
              <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-primary" />
            ) : null}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Chat threads</span>
            <button
              type="button"
              onClick={() => setShowArchived((s) => !s)}
              className="text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {visible.length === 0 ? (
            <div className="px-2 py-3 text-xs text-muted-foreground">
              No threads yet — start one below.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {visible.map((t) => {
                const isActive = t.id === activeThreadId;
                return (
                  <div
                    key={t.id}
                    className={cn(
                      "group/thread flex items-center gap-1 px-1.5 py-0.5",
                      isActive ? "bg-accent/50" : "hover:bg-accent/30",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(t.id)}
                      className="flex-1 truncate rounded px-1.5 py-1 text-left text-xs"
                    >
                      <div className="flex items-center gap-1.5">
                        {isActive ? (
                          <Check className="h-3 w-3 shrink-0 text-primary" />
                        ) : t.unread ? (
                          <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        ) : (
                          <span className="inline-block h-1.5 w-1.5 shrink-0" />
                        )}
                        <span className={cn("truncate", t.archived_at && "italic opacity-60")}>
                          {threadLabel(t)}
                        </span>
                      </div>
                      {t.last_message_snippet ? (
                        <p className="ml-4 mt-0.5 truncate text-[11px] text-muted-foreground">
                          {t.last_message_snippet}
                        </p>
                      ) : null}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleArchive(t)}
                      className="rounded p-1 text-muted-foreground opacity-0 hover:bg-background hover:text-foreground group-hover/thread:opacity-100"
                      title={t.archived_at ? "Unarchive" : "Archive"}
                    >
                      {t.archived_at ? (
                        <RotateCcw className="h-3 w-3" />
                      ) : (
                        <Archive className="h-3 w-3" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(t)}
                      className="rounded p-1 text-muted-foreground opacity-0 hover:bg-background hover:text-destructive group-hover/thread:opacity-100"
                      title="Delete thread"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={creating} onSelect={(e) => { e.preventDefault(); void handleNew(); }}>
            {creating ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-2 h-3.5 w-3.5" />}
            New thread
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!active} onSelect={(e) => { e.preventDefault(); startRename(); }}>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Rename current
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Rename thread</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Thread title"
            maxLength={200}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)} disabled={renaming}>
              Cancel
            </Button>
            <Button onClick={submitRename} disabled={renaming}>
              {renaming ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
